import React, { useCallback, useEffect, useState } from "react";
import { useAuthUser } from "../auth/AuthUserContext";
import { getUserSuggestions } from "../api/usersApi";

/**
 * @param {object} props
 * @param {"full" | "compact"} props.variant
 * @param {(user: { userId: number, username: string }) => void} [props.onPickUser]
 * @param {string} [props.className]
 */
function UserSuggestionsPanel({ variant = "full", onPickUser, className = "" }) {
  const me = useAuthUser();
  const userId = typeof me?.userId === "number" ? me.userId : null;
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (search) => {
    if (userId == null) return;
    setLoading(true);
    try {
      const { response, data } = await getUserSuggestions({
        q: search.trim() || undefined,
        take: variant === "compact" ? 24 : 100
      });
      if (!response.ok) {
        setItems([]);
        return;
      }
      const raw = Array.isArray(data) ? data : [];
      setItems(
        raw.map((row) => ({
          userId: row.userId ?? row.UserId,
          username: (row.username ?? row.Username ?? "").toString()
        })).filter((x) => x.username)
      );
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [userId, variant]);

  useEffect(() => {
    if (userId == null) return undefined;
    const t = setTimeout(() => {
      void load(q);
    }, variant === "compact" && q === "" ? 0 : 280);
    return () => clearTimeout(t);
  }, [q, load, userId, variant]);

  if (userId == null) {
    if (variant === "compact") return null;
    return (
      <div className={`user-suggest-panel user-suggest-panel--full ${className}`.trim()}>
        <p className="muted small" style={{ margin: 0 }}>
          Sign in to browse recently joined WillPlay players and add them in chat.
        </p>
      </div>
    );
  }

  const handlePick = (row) => {
    if (onPickUser) onPickUser(row);
  };

  if (variant === "compact") {
    return (
      <div className={`user-suggest-panel user-suggest-panel--compact ${className}`.trim()}>
        <p className="user-suggest-panel__compact-title">New on WillPlay</p>
        <div className="user-suggest-panel__chips" role="list">
          {loading && items.length === 0 ? (
            <span className="muted small">Loading…</span>
          ) : items.length === 0 ? (
            <span className="muted small">No recently joined users yet.</span>
          ) : (
            items.map((row) => (
              <button
                key={row.userId}
                type="button"
                role="listitem"
                className="user-suggest-panel__chip"
                onClick={() => handlePick(row)}
              >
                {row.username}
              </button>
            ))
          )}
        </div>
      </div>
    );
  }

  return (
    <aside className={`user-suggest-panel user-suggest-panel--full ${className}`.trim()} aria-label="Player suggestions">
      <h3 className="user-suggest-panel__heading">Recently joined</h3>
      <p className="user-suggest-panel__hint muted small">
        Only users who joined within the last 24 hours appear here. Tap a name to add as a friend.
      </p>
      <input
        type="search"
        className="user-suggest-panel__search"
        placeholder="Search by username…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        autoComplete="off"
        aria-label="Search players"
      />
      <div className="user-suggest-panel__list-wrap">
        {loading && items.length === 0 ? (
          <p className="muted small">Loading…</p>
        ) : items.length === 0 ? (
          <p className="muted small">No recently joined users match right now.</p>
        ) : (
          <ul className="user-suggest-panel__list">
            {items.map((row) => (
              <li key={row.userId}>
                <button
                  type="button"
                  className="user-suggest-panel__row"
                  onClick={() => handlePick(row)}
                >
                  <span className="user-suggest-panel__name">{row.username}</span>
                  <span className="user-suggest-panel__action">Add</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </aside>
  );
}

export default UserSuggestionsPanel;
